import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Textarea,
  useMantineColorScheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import isoWeek from "dayjs/plugin/isoWeek";
import { Check, ChevronRight, ExternalLink, Info, MessageSquare, RotateCcw } from "lucide-react";
import {
  bulkApproveTimesheetsLR,
  bulkApproveTimesheetsPM,
  fetchTimesheetApprovalQueue,
  fetchTimesheetDetail,
  fetchTimesheetPMApprovalQueue,
  lrApproveTimesheet,
  lrReturnTimesheet,
  pmApproveTimesheet,
  pmReturnTimesheet,
  type BulkApproveResult,
  type TimesheetEntry,
  type TimesheetWeekDetail,
  type TimesheetWeekSummary,
} from "../api/client";
import { useBadges } from "../hooks/BadgesContext";
import {
  useQueueSort,
  type SortAccessors,
  type SortDirection,
  type SortState,
} from "../hooks/useQueueSort";
import type { Scheme, StatusSlot } from "../theme";
import { FONTS, NEUTRALS, PROJECT_PALETTE, STATUS } from "../theme/tokens";
import { CellCommentHint } from "./CellCommentHint";
import { Linkify } from "./Linkify";
import { StatusBadge, type TimesheetStatus } from "./StatusBadge";

dayjs.extend(isoWeek);
dayjs.locale("ru");

export type ApprovalVariant = "lr" | "pm";

interface VariantConfig {
  title: string;
  fetchQueue: () => Promise<TimesheetWeekSummary[]>;
  approve: (id: number) => Promise<unknown>;
  approveReturn: (id: number, comment: string) => Promise<unknown>;
  bulkApprove: (ids: number[]) => Promise<BulkApproveResult>;
  accessDeniedText: string;
  approveLabelLong: string;
}

const VARIANTS: Record<ApprovalVariant, VariantConfig> = {
  lr: {
    title: "Недели на согласование",
    fetchQueue: fetchTimesheetApprovalQueue,
    approve: lrApproveTimesheet,
    approveReturn: lrReturnTimesheet,
    bulkApprove: bulkApproveTimesheetsLR,
    accessDeniedText: "Нет подчинённых — очередь согласования недоступна.",
    approveLabelLong: "Согласовать и отправить PM",
  },
  pm: {
    title: "Недели ваших проектов",
    fetchQueue: fetchTimesheetPMApprovalQueue,
    approve: pmApproveTimesheet,
    approveReturn: pmReturnTimesheet,
    bulkApprove: bulkApproveTimesheetsPM,
    accessDeniedText: "Нет проектов под вашим управлением — очередь PM-согласования недоступна.",
    approveLabelLong: "Согласовать неделю",
  },
};

function formatWeek(iso: string): string {
  const d = dayjs(iso);
  if (!d.isValid()) return iso;
  const end = d.add(6, "day");
  return `${d.format("DD.MM")}—${end.format("DD.MM.YYYY")}`;
}

function formatWeekShort(iso: string): string {
  const d = dayjs(iso);
  if (!d.isValid()) return iso;
  return `W${String(d.isoWeek()).padStart(2, "0")} · ${d.format("DD.MM")}—${d.add(6, "day").format("DD.MM")}`;
}

function formatSubmittedAt(iso: string | null): string {
  if (!iso) return "—";
  const d = dayjs(iso);
  return d.isValid() ? d.format("DD MMM HH:mm") : iso;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function avatarColor(name: string, scheme: Scheme): { bg: string; fg: string } {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % 360;
  return scheme === "dark"
    ? { bg: `hsl(${h}, 22%, 28%)`, fg: `hsl(${h}, 30%, 78%)` }
    : { bg: `hsl(${h}, 35%, 88%)`, fg: `hsl(${h}, 45%, 32%)` };
}

interface FlagDef {
  key: "underflow" | "overflow";
  label: string;
  slot: StatusSlot;
}

function computeFlags(w: TimesheetWeekSummary): FlagDef[] {
  const required = parseFloat(w.required_hours);
  const allocated = parseFloat(w.allocated_hours);
  // US-74.1: effective_required = календарная норма МИНУС approved-отсутствия.
  // Иначе при отпуске Вт+Ср PM видит «16 / 32, Меньше нормы», хотя сотрудник
  // списал ровно столько, сколько от него требуется (32 − 16 = 16).
  // `absence_hours` пришло из backend через US-74.1 serializer; для старых ответов
  // (legacy) treat as "0.00".
  const absenceHours = parseFloat(w.absence_hours ?? "0") || 0;
  const effectiveRequired = Math.max(0, required - absenceHours);
  if (!Number.isFinite(effectiveRequired) || !Number.isFinite(allocated)) return [];
  if (allocated < effectiveRequired) {
    return [{ key: "underflow", label: "Меньше нормы", slot: "rework" }];
  }
  if (allocated > effectiveRequired) {
    return [{ key: "overflow", label: "Переработка", slot: "waiting" }];
  }
  return [];
}

interface ProjectGroup {
  projectId: number;
  code: string;
  name: string;
  color: string;
  hours: number;
  comments: string[];
}

// US-43: day-grid (Пн×Пт × проекты) для очереди согласования.
// Раньше комменты выводились бесадресным списком — ЛР/PM не знал, к какому
// дню относится. Теперь grid: строка = проект, колонка = день. В ячейке —
// часы + иконка коммента (tooltip), как в xlsx-экспорте.

const DAY_LABELS_SHORT = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

interface DayCell {
  hours: number;
  comments: string[]; // несколько entries на одну ячейку (если такое случилось) → склеиваем
}

interface DayGridRow {
  projectId: number;
  code: string;
  name: string;
  color: string;
  /** 7 ячеек, индекс 0 = понедельник недели. */
  cells: DayCell[];
  total: number;
}

function buildDayGrid(
  entries: TimesheetEntry[],
  weekStart: string,
): { rows: DayGridRow[]; hasWeekendEntries: boolean } {
  const monday = dayjs(weekStart).startOf("day");
  const rowsMap = new Map<number, DayGridRow>();
  let hasWeekendEntries = false;
  for (const e of entries) {
    const hours = parseFloat(e.hours);
    if (!Number.isFinite(hours) || hours <= 0) continue;
    const dayIdx = dayjs(e.work_date).startOf("day").diff(monday, "day");
    if (dayIdx < 0 || dayIdx > 6) continue;
    if (dayIdx >= 5) hasWeekendEntries = true;
    let row = rowsMap.get(e.project);
    if (!row) {
      row = {
        projectId: e.project,
        code: e.project_code,
        name: e.project_name,
        color: e.project_color || PROJECT_PALETTE[0].hex,
        cells: Array.from({ length: 7 }, () => ({ hours: 0, comments: [] })),
        total: 0,
      };
      rowsMap.set(e.project, row);
    }
    row.cells[dayIdx].hours += hours;
    const comment = (e.comment ?? "").trim();
    if (comment && !row.cells[dayIdx].comments.includes(comment)) {
      row.cells[dayIdx].comments.push(comment);
    }
    row.total += hours;
  }
  const rows = Array.from(rowsMap.values()).sort((a, b) => a.code.localeCompare(b.code));
  return { rows, hasWeekendEntries };
}

function formatGridHours(n: number): string {
  if (!n) return "—";
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1).replace(".", ",");
}

interface DayGridProps {
  entries: TimesheetEntry[];
  weekStart: string;
  scheme: Scheme;
}

interface CommentEntry {
  rowId: number;
  colIdx: number;
  projectCode: string;
  projectName: string;
  projectColor: string;
  dayLabel: string;
  dateLabel: string;
  hours: number;
  texts: string[];
}

/** Собирает все комменты из rows в плоский список, отсортированный пн→вс,
 *  внутри дня — по project code. Используется в jump-to-comment панели. */
function collectComments(rows: DayGridRow[], weekStart: string, numDays: number): CommentEntry[] {
  const monday = dayjs(weekStart).startOf("day");
  const list: CommentEntry[] = [];
  for (let di = 0; di < numDays; di += 1) {
    const dayRows = rows
      .filter((r) => r.cells[di].comments.length > 0)
      .sort((a, b) => a.code.localeCompare(b.code));
    for (const r of dayRows) {
      const cell = r.cells[di];
      list.push({
        rowId: r.projectId,
        colIdx: di,
        projectCode: r.code,
        projectName: r.name,
        projectColor: r.color,
        dayLabel: DAY_LABELS_SHORT[di],
        dateLabel: monday.add(di, "day").format("DD.MM"),
        hours: cell.hours,
        texts: cell.comments,
      });
    }
  }
  return list;
}

function DayGrid({ entries, weekStart, scheme }: DayGridProps) {
  const n = NEUTRALS[scheme];
  const { rows, hasWeekendEntries } = useMemo(
    () => buildDayGrid(entries, weekStart),
    [entries, weekStart],
  );
  const monday = dayjs(weekStart).startOf("day");
  const numDays = hasWeekendEntries ? 7 : 5;
  // Header dates per visible day.
  const dayDates = Array.from({ length: numDays }, (_, i) => monday.add(i, "day"));

  // US-43 polish: hover state для подсветки строки/колонки.
  // rowId === null → не наведено; иначе projectId (число) — индекс проекта.
  // colIdx === null → не наведено; иначе 0..6 — индекс дня.
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [hoveredCol, setHoveredCol] = useState<number | null>(null);

  // US-43 polish: collapsible jump-to-comment панель.
  const [commentsOpen, setCommentsOpen] = useState(false);

  const commentList = useMemo(
    () => collectComments(rows, weekStart, numDays),
    [rows, weekStart, numDays],
  );

  if (rows.length === 0) {
    return (
      <Text c="dimmed" size="sm" ta="center" py="md">
        Нет распределения по дням.
      </Text>
    );
  }

  // Day-column totals across all projects.
  const dayTotals = Array.from({ length: numDays }, (_, i) =>
    rows.reduce((s, r) => s + r.cells[i].hours, 0),
  );
  const weekTotal = dayTotals.reduce((s, h) => s + h, 0);

  const gridCols = `minmax(180px, 1.8fr) repeat(${numDays}, minmax(46px, 1fr)) 64px`;

  const cellBase = {
    padding: "6px 4px",
    textAlign: "center" as const,
    fontVariantNumeric: "tabular-nums" as const,
    fontFamily: FONTS.mono,
    fontSize: 12.5,
    borderRight: `1px solid ${n.border}`,
  };

  // Подсветка строки/колонки. Cross-cell (intersection) — чуть сильнее.
  // Используем синеватый полупрозрачный overlay поверх любого фона —
  // одинаково работает и на header (n.surfaceAlt), и на rows (transparent),
  // и в обеих темах (на dark фоне всё ещё контрастирует).
  const HL_SOFT = "rgba(91, 107, 224, 0.08)";
  const HL_STRONG = "rgba(91, 107, 224, 0.18)";

  function hoverBg(
    rowId: number | "header" | "footer",
    colIdx: number | "label" | "total",
    base: string,
  ): string {
    const rowHit = typeof rowId === "number" && rowId === hoveredRow;
    const colHit = typeof colIdx === "number" && colIdx === hoveredCol;
    if (rowHit && colHit) return HL_STRONG;
    if (rowHit || colHit) return HL_SOFT;
    return base;
  }

  return (
    <Stack
      gap={8}
      data-testid="approval-day-grid-root"
      data-hovered-row={hoveredRow ?? ""}
      data-hovered-col={hoveredCol ?? ""}
    >
      {/* US-43 polish: jump-to-comment панель. Кнопка-плашка раскрывается
          в список «день · проект · часы → текст», отсортированный пн→вс. */}
      {commentList.length > 0 && (
        <div
          data-testid="approval-day-grid-comments-panel"
          style={{
            border: `1px solid ${n.border}`,
            borderRadius: 8,
            background: n.surfaceAlt,
            fontSize: 12.5,
          }}
        >
          <button
            type="button"
            data-testid="approval-day-grid-comments-toggle"
            onClick={() => setCommentsOpen((v) => !v)}
            style={{
              appearance: "none",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 12px",
              color: n.text,
              fontFamily: "inherit",
              fontSize: "inherit",
              fontWeight: 600,
            }}
            aria-expanded={commentsOpen}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                color: n.text,
              }}
            >
              <MessageSquare size={13} style={{ color: n.textMuted }} />
              Комментарии · {commentList.length}
            </span>
            <ChevronRight
              size={14}
              style={{
                color: n.textMuted,
                transform: commentsOpen ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 120ms ease",
              }}
            />
          </button>
          {commentsOpen && (
            <ul
              data-testid="approval-day-grid-comments-list"
              style={{
                listStyle: "none",
                margin: 0,
                padding: "4px 0 8px",
                borderTop: `1px solid ${n.border}`,
                background: n.surface,
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
              }}
            >
              {commentList.map((c, i) => (
                <li
                  key={`${c.rowId}-${c.colIdx}-${i}`}
                  data-testid={`approval-day-grid-comment-item-${c.rowId}-${c.colIdx}`}
                  style={{
                    padding: "8px 12px",
                    borderTop: i === 0 ? "none" : `1px solid ${n.border}`,
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gap: 10,
                    alignItems: "start",
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 3,
                      borderRadius: 1.5,
                      background: c.projectColor,
                      alignSelf: "stretch",
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "baseline",
                        flexWrap: "wrap",
                        marginBottom: 2,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          color: n.text,
                          fontSize: 12,
                        }}
                      >
                        {c.dayLabel} · {c.dateLabel}
                      </span>
                      <span
                        style={{
                          fontFamily: FONTS.mono,
                          fontSize: 11.5,
                          color: n.textMuted,
                        }}
                      >
                        {c.projectCode}
                      </span>
                      <span style={{ fontSize: 11.5, color: n.textDim }}>{c.projectName}</span>
                      <span
                        style={{
                          marginLeft: "auto",
                          fontFamily: FONTS.mono,
                          fontSize: 11.5,
                          color: n.textMuted,
                        }}
                      >
                        {formatGridHours(c.hours)} ч
                      </span>
                    </div>
                    {c.texts.map((t, j) => (
                      <div
                        key={j}
                        style={{
                          fontSize: 12.5,
                          color: n.text,
                          lineHeight: 1.45,
                          fontStyle: "italic",
                        }}
                      >
                        «<Linkify text={t} />»
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div
        data-testid="approval-day-grid"
        style={{
          border: `1px solid ${n.border}`,
          borderRadius: 8,
          overflow: "auto",
          maxHeight: 480,
        }}
      >
        {/* Header row — sticky сверху, чтобы при скролле длинного списка
            проектов заголовки дней оставались видимы. */}
        <div
          data-testid="approval-day-grid-header"
          style={{
            display: "grid",
            gridTemplateColumns: gridCols,
            background: n.surfaceAlt,
            borderBottom: `1px solid ${n.border}`,
            fontSize: 11.5,
            color: n.textMuted,
            fontWeight: 600,
            position: "sticky",
            top: 0,
            zIndex: 2,
          }}
        >
          <div
            style={{
              padding: "8px 12px",
              borderRight: `1px solid ${n.border}`,
              background: hoverBg("header", "label", n.surfaceAlt),
            }}
          >
            Проект
          </div>
          {dayDates.map((d, i) => (
            <div
              key={i}
              data-day-idx={i}
              data-testid={`approval-day-grid-header-day-${i}`}
              onMouseEnter={() => setHoveredCol(i)}
              onMouseLeave={() => setHoveredCol(null)}
              style={{
                ...cellBase,
                padding: "8px 4px",
                color: i >= 5 ? n.textDim : n.textMuted,
                background: hoverBg("header", i, n.surfaceAlt),
              }}
            >
              <div>{DAY_LABELS_SHORT[i]}</div>
              <div style={{ fontSize: 10, opacity: 0.7 }}>{d.format("DD.MM")}</div>
            </div>
          ))}
          <div
            style={{
              padding: "8px 4px",
              textAlign: "center",
              background: hoverBg("header", "total", n.surfaceAlt),
            }}
          >
            Итого
          </div>
        </div>

        {/* Project rows */}
        {rows.map((row) => (
          <div
            key={row.projectId}
            data-testid={`approval-day-grid-row-${row.projectId}`}
            style={{
              display: "grid",
              gridTemplateColumns: gridCols,
              borderBottom: `1px solid ${n.border}`,
            }}
          >
            <div
              onMouseEnter={() => setHoveredRow(row.projectId)}
              onMouseLeave={() => setHoveredRow(null)}
              style={{
                padding: "6px 12px",
                borderRight: `1px solid ${n.border}`,
                display: "flex",
                alignItems: "center",
                gap: 8,
                minWidth: 0,
                background: hoverBg(row.projectId, "label", "transparent"),
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 3,
                  height: 18,
                  borderRadius: 1.5,
                  background: row.color,
                  flexShrink: 0,
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: n.text,
                  }}
                >
                  {row.name}
                </div>
                <div style={{ fontSize: 10.5, color: n.textDim, fontFamily: FONTS.mono }}>
                  {row.code}
                </div>
              </div>
            </div>
            {row.cells.slice(0, numDays).map((cell, di) => (
              <div
                key={di}
                onMouseEnter={() => {
                  setHoveredRow(row.projectId);
                  setHoveredCol(di);
                }}
                onMouseLeave={() => {
                  setHoveredRow(null);
                  setHoveredCol(null);
                }}
                style={{
                  ...cellBase,
                  color: cell.hours ? n.text : n.textDim,
                  fontWeight: cell.hours ? 500 : 400,
                  background: hoverBg(row.projectId, di, di >= 5 ? n.surfaceAlt : "transparent"),
                  position: "relative",
                  transition: "background-color 80ms ease",
                }}
              >
                <span>{formatGridHours(cell.hours)}</span>
                {/* US-86.2: режим (hovercard/tooltip) инкапсулирован в
                    CellCommentHint — откат к тултипу = одна строка там. */}
                <CellCommentHint
                  comments={cell.comments}
                  iconColor={n.textMuted}
                  testId={`approval-day-grid-comment-${row.projectId}-${di}`}
                />
              </div>
            ))}
            <div
              onMouseEnter={() => setHoveredRow(row.projectId)}
              onMouseLeave={() => setHoveredRow(null)}
              style={{
                ...cellBase,
                borderRight: "none",
                fontWeight: 600,
                background: hoverBg(row.projectId, "total", "transparent"),
              }}
            >
              {formatGridHours(row.total)}
            </div>
          </div>
        ))}

        {/* Totals footer */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: gridCols,
            background: n.surfaceAlt,
            fontSize: 12.5,
            fontWeight: 600,
          }}
        >
          <div
            style={{
              padding: "8px 12px",
              borderRight: `1px solid ${n.border}`,
              color: n.textMuted,
              background: hoverBg("footer", "label", n.surfaceAlt),
            }}
          >
            Итого
          </div>
          {dayTotals.map((h, i) => (
            <div
              key={i}
              onMouseEnter={() => setHoveredCol(i)}
              onMouseLeave={() => setHoveredCol(null)}
              style={{
                ...cellBase,
                color: h ? n.text : n.textDim,
                background: hoverBg("footer", i, n.surfaceAlt),
              }}
            >
              {formatGridHours(h)}
            </div>
          ))}
          <div
            style={{
              ...cellBase,
              borderRight: "none",
              background: hoverBg("footer", "total", n.surfaceAlt),
            }}
          >
            {formatGridHours(weekTotal)}
          </div>
        </div>
      </div>
    </Stack>
  );
}

function groupEntriesByProject(entries: TimesheetEntry[]): ProjectGroup[] {
  const map = new Map<number, ProjectGroup>();
  for (const e of entries) {
    const hours = parseFloat(e.hours);
    if (!Number.isFinite(hours)) continue;
    const existing = map.get(e.project);
    const comment = (e.comment ?? "").trim();
    if (existing) {
      existing.hours += hours;
      if (comment && !existing.comments.includes(comment)) {
        existing.comments.push(comment);
      }
    } else {
      map.set(e.project, {
        projectId: e.project,
        code: e.project_code,
        name: e.project_name,
        color: e.project_color || PROJECT_PALETTE[0].hex,
        hours,
        comments: comment ? [comment] : [],
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.hours - a.hours);
}

interface ReturnModalProps {
  week: TimesheetWeekSummary | null;
  onClose: () => void;
  onConfirm: (comment: string) => void;
  busy: boolean;
}

function ReturnModal({ week, onClose, onConfirm, busy }: ReturnModalProps) {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const [comment, setComment] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (week) {
      setComment("");
      setTouched(false);
    }
  }, [week]);

  const error = touched && comment.trim() === "";

  return (
    <Modal
      opened={!!week}
      onClose={onClose}
      title="Вернуть неделю на доработку"
      centered={!isMobile}
      fullScreen={isMobile}
      // US-74 hotfix: модалка открывается ПОВЕРХ ApprovalDrawer (Mantine Drawer
      // имеет z-index 200 по умолчанию). Без явного zIndex Modal рендерится
      // под overlay-ом drawer-а и видится заблюренной — клик «активирует» её.
      // Поднимаем над drawer-ом, чтобы overlay/контент были интерактивны сразу.
      zIndex={400}
    >
      <Stack gap="sm">
        <Textarea
          label="Комментарий"
          placeholder="Опишите, что нужно исправить"
          autosize
          minRows={3}
          maxRows={8}
          value={comment}
          onChange={(e) => {
            setComment(e.currentTarget.value);
            setTouched(true);
          }}
          error={error ? "Комментарий обязателен" : undefined}
          required
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button
            color="red"
            onClick={() => {
              if (comment.trim() === "") {
                setTouched(true);
                return;
              }
              onConfirm(comment.trim());
            }}
            loading={busy}
            disabled={busy}
          >
            Вернуть
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface ApprovalDrawerProps {
  week: TimesheetWeekSummary | null;
  variant: ApprovalVariant;
  busy: boolean;
  onClose: () => void;
  onApprove: (week: TimesheetWeekSummary) => void;
  onReturn: (week: TimesheetWeekSummary) => void;
}

function ApprovalDrawer({
  week,
  variant,
  busy,
  onClose,
  onApprove,
  onReturn,
}: ApprovalDrawerProps) {
  const { colorScheme } = useMantineColorScheme();
  const scheme: Scheme = colorScheme === "dark" ? "dark" : "light";
  const n = NEUTRALS[scheme];
  const config = VARIANTS[variant];
  // На телефоне drawer занимает весь экран (иначе 640px шире вьюпорта), а панель
  // действий переносит кнопки под пояснение.
  const isMobile = useMediaQuery("(max-width: 48em)");

  const [detail, setDetail] = useState<TimesheetWeekDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!week) {
      setDetail(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchTimesheetDetail(week.id)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [week]);

  const projects = useMemo(() => (detail ? groupEntriesByProject(detail.entries) : []), [detail]);
  const calendarRequired = week ? parseFloat(week.required_hours) : 0;
  const allocated = week ? parseFloat(week.allocated_hours) : 0;
  // US-74.1: effective_required вычитает approved-отсутствия из календарной нормы.
  const absenceHours = week ? parseFloat(week.absence_hours ?? "0") || 0 : 0;
  const required = Math.max(0, calendarRequired - absenceHours);
  const max = Math.max(required, allocated, 1);
  const projectsWord = (n: number) => {
    if (n === 0) return "проектов";
    if (n === 1) return "проект";
    if (n >= 2 && n <= 4) return "проекта";
    return "проектов";
  };

  return (
    <Drawer
      opened={!!week}
      onClose={onClose}
      position={isMobile ? "bottom" : "right"}
      size={isMobile ? "100%" : 640}
      withCloseButton={false}
      padding={0}
      keepMounted={false}
      transitionProps={{ duration: 0 }}
      overlayProps={{ backgroundOpacity: 0.45, blur: 2 }}
      styles={{
        content: { background: n.bg, display: "flex", flexDirection: "column" },
        body: { padding: 0, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 },
      }}
    >
      {week ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            background: n.bg,
          }}
        >
          {/* Breadcrumb header. На телефоне прячем первые сегменты крошек —
              иначе строка не влезает и «Закрыть» обрезается. */}
          <div
            style={{
              padding: isMobile ? "12px 16px" : "14px 22px",
              borderBottom: `1px solid ${n.border}`,
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              color: n.textDim,
              background: n.surface,
            }}
          >
            {!isMobile && (
              <>
                <span>На согласовании</span>
                <ChevronRight size={11} />
                <span>{config.title}</span>
                <ChevronRight size={11} />
              </>
            )}
            <span style={{ color: n.text, fontWeight: 500, minWidth: 0 }}>
              {week.employee_name} · {formatWeekShort(week.week_start)}
            </span>
            <div style={{ flex: 1 }} />
            <Button variant="subtle" size="xs" onClick={onClose} style={{ flexShrink: 0 }}>
              Закрыть
            </Button>
          </div>

          {/* Scrollable body */}
          <div style={{ flex: 1, overflow: "auto", padding: 22 }}>
            {/* Employee header */}
            <div
              style={{
                display: "flex",
                gap: 14,
                alignItems: "center",
                marginBottom: 14,
                padding: "14px 18px",
                background: n.surface,
                border: `1px solid ${n.border}`,
                borderRadius: 8,
              }}
            >
              <Avatar name={week.employee_name} scheme={scheme} size={44} fontSize={15} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 2 }}>
                  {week.employee_name}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: n.textMuted,
                    fontFamily: FONTS.mono,
                  }}
                >
                  ID {week.employee} · {formatWeekShort(week.week_start)}
                </div>
              </div>
              <StatusBadge
                entity="timesheet"
                code={week.status as TimesheetStatus}
                label={week.status_display}
                size="md"
              />
            </div>

            {/* Meta strip */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 1,
                marginBottom: 14,
                background: n.border,
                border: `1px solid ${n.border}`,
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              {[
                {
                  label: "Неделя",
                  value: formatWeekShort(week.week_start),
                  mono: true,
                },
                {
                  label: "Норма",
                  value: `${required.toFixed(1)} ч`,
                },
                {
                  label: "Списано",
                  value: `${allocated.toFixed(1)} ч`,
                  accent: allocated >= required ? "ok" : "warn",
                },
                {
                  label: "Отправлен",
                  value: formatSubmittedAt(week.submitted_at),
                  mono: true,
                },
              ].map((c) => (
                <div key={c.label} style={{ background: n.surface, padding: "10px 14px" }}>
                  <div
                    style={{
                      fontSize: 10.5,
                      color: n.textMuted,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      marginBottom: 3,
                    }}
                  >
                    {c.label}
                  </div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13.5,
                      fontFamily: c.mono ? FONTS.mono : "inherit",
                      color:
                        c.accent === "ok"
                          ? STATUS[scheme].approved.fg
                          : c.accent === "warn"
                            ? STATUS[scheme].rework.fg
                            : n.text,
                    }}
                  >
                    {c.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Comments banner if returns happened */}
            {(week.lr_comment || week.pm_comment) && (
              <div
                style={{
                  marginBottom: 14,
                  padding: "10px 14px",
                  background: STATUS[scheme].rework.bg,
                  border: `1px solid ${STATUS[scheme].rework.border}`,
                  color: STATUS[scheme].rework.fg,
                  borderRadius: 8,
                  fontSize: 12.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                {week.lr_comment && (
                  <div>
                    <b>ЛР:</b> {week.lr_comment}
                  </div>
                )}
                {week.pm_comment && (
                  <div>
                    <b>PM:</b> {week.pm_comment}
                  </div>
                )}
              </div>
            )}

            {/* US-43: day×project grid с per-cell hours+comment-tooltip. */}
            {!loading && !error && detail && (
              <div
                style={{
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 6,
                    color: n.text,
                  }}
                >
                  По дням
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 11,
                      fontWeight: 400,
                      color: n.textMuted,
                    }}
                  >
                    наведите на{" "}
                    <MessageSquare size={10} style={{ verticalAlign: "middle", margin: "0 2px" }} />{" "}
                    чтобы увидеть комментарий
                  </span>
                </div>
                <DayGrid entries={detail.entries} weekStart={detail.week_start} scheme={scheme} />
              </div>
            )}

            {/* Project distribution */}
            <div
              style={{
                border: `1px solid ${n.border}`,
                borderRadius: 8,
                background: n.surface,
                overflow: "hidden",
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  padding: "10px 14px",
                  borderBottom: `1px solid ${n.border}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 13 }}>Распределение по проектам</span>
                <span
                  style={{
                    padding: "2px 7px",
                    borderRadius: 4,
                    background: n.surfaceAlt,
                    color: n.textMuted,
                    fontFamily: FONTS.mono,
                    fontSize: 10.5,
                  }}
                >
                  readonly
                </span>
                <div style={{ flex: 1 }} />
                {!loading && detail && (
                  <span
                    style={{
                      fontSize: 11.5,
                      color: n.textDim,
                      fontFamily: FONTS.mono,
                    }}
                  >
                    {projects.length} {projectsWord(projects.length)}
                  </span>
                )}
              </div>
              <div style={{ padding: 12 }}>
                {loading ? (
                  <Group justify="center" py="md">
                    <Loader size="sm" />
                  </Group>
                ) : error ? (
                  <Alert color="red" variant="light">
                    Не удалось загрузить детали недели: {error}
                  </Alert>
                ) : projects.length === 0 ? (
                  <Text c="dimmed" size="sm" ta="center" py="md">
                    Нет распределения по проектам.
                  </Text>
                ) : (
                  projects.map((p, i) => (
                    <div
                      key={p.projectId}
                      style={{
                        padding: "8px 4px",
                        borderBottom: i === projects.length - 1 ? "none" : `1px solid ${n.border}`,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <span
                          style={{
                            width: 3,
                            height: 22,
                            borderRadius: 1.5,
                            background: p.color,
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {p.name}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: n.textDim,
                              fontFamily: FONTS.mono,
                            }}
                          >
                            {p.code}
                          </div>
                        </div>
                        <div
                          style={{
                            width: 180,
                            height: 6,
                            background: n.surfaceAlt,
                            borderRadius: 999,
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(100, (p.hours / max) * 100)}%`,
                              height: "100%",
                              background: p.color,
                            }}
                          />
                        </div>
                        <div
                          style={{
                            width: 64,
                            textAlign: "right",
                            fontFamily: FONTS.mono,
                            fontWeight: 600,
                            fontSize: 13,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {p.hours.toFixed(1)} ч
                        </div>
                      </div>
                      {p.comments.length > 0 && (
                        <ul
                          data-testid="project-entry-comments"
                          style={{
                            margin: "6px 0 0 15px",
                            padding: 0,
                            listStyle: "disc",
                            color: n.textMuted,
                            fontSize: 11.5,
                            lineHeight: 1.4,
                          }}
                        >
                          {p.comments.map((c, ci) => (
                            <li key={ci} style={{ marginLeft: 12 }}>
                              {/* US-86: Jira-ссылки в комментарии — кликабельны. */}
                              <Linkify text={c} />
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Absence rows reminder */}
            {detail && detail.absences.length > 0 && (
              <div
                style={{
                  marginBottom: 14,
                  padding: "10px 14px",
                  background: STATUS[scheme].absence.bg,
                  border: `1px solid ${STATUS[scheme].absence.border}`,
                  color: STATUS[scheme].absence.fg,
                  borderRadius: 8,
                  fontSize: 12.5,
                }}
              >
                В неделе {detail.absences.length}{" "}
                {detail.absences.length === 1
                  ? "согласованное отсутствие"
                  : "согласованных отсутствий"}
                .
              </div>
            )}
          </div>

          {/* Sticky action panel */}
          <div
            style={{
              padding: isMobile ? "12px 16px" : "14px 22px",
              background: n.surface,
              borderTop: `1px solid ${n.border}`,
              display: "flex",
              gap: 12,
              alignItems: "center",
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}
          >
            {!isMobile && <Info size={16} color={n.textMuted} />}
            <div
              style={{
                flex: isMobile ? "0 0 100%" : 1,
                fontSize: 12,
                color: n.textMuted,
              }}
            >
              {variant === "lr"
                ? "После одобрения неделя уйдёт PM проектов на согласование."
                : "После одобрения неделя получит финальный статус, если все PM согласовали."}
            </div>
            <Button
              variant="default"
              leftSection={<RotateCcw size={13} />}
              onClick={() => onReturn(week)}
              disabled={busy}
              style={isMobile ? { flex: 1 } : undefined}
            >
              Вернуть
            </Button>
            <Button
              color="green"
              leftSection={<Check size={13} />}
              onClick={() => onApprove(week)}
              loading={busy}
              disabled={busy}
              style={isMobile ? { flex: 2 } : undefined}
            >
              {config.approveLabelLong}
            </Button>
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}

interface AvatarProps {
  name: string;
  scheme: Scheme;
  size?: number;
  fontSize?: number;
}

function Avatar({ name, scheme, size = 28, fontSize }: AvatarProps) {
  const init = getInitials(name);
  const { bg, fg } = avatarColor(name, scheme);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        background: bg,
        color: fg,
        fontSize: fontSize ?? size * 0.42,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {init}
    </div>
  );
}

interface FlagChipProps {
  flag: FlagDef;
  scheme: Scheme;
}

function FlagChip({ flag, scheme }: FlagChipProps) {
  const tok = STATUS[scheme][flag.slot];
  return (
    <span
      data-testid={`flag-${flag.key}`}
      style={{
        padding: "2px 7px",
        borderRadius: 4,
        background: tok.bg,
        color: tok.fg,
        border: `1px solid ${tok.border}`,
        fontSize: 10.5,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {flag.label}
    </span>
  );
}

interface DelegationBadgeProps {
  scheme: Scheme;
}

function DelegationBadge({ scheme }: DelegationBadgeProps) {
  const tok = STATUS[scheme].absence;
  return (
    <span
      title="Эта неделя попала в вашу очередь через делегирование."
      style={{
        padding: "2px 7px",
        borderRadius: 4,
        background: tok.bg,
        color: tok.fg,
        border: `1px solid ${tok.border}`,
        fontSize: 10.5,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      Передано делегату
    </span>
  );
}

/**
 * US-110: неделю заполнила система, а не человек.
 *
 * Зачем бейдж. Руководитель проекта согласует часы, которых сотрудник не
 * вводил и мог не видеть. Если такие недели неотличимы от обычных,
 * согласование перестаёт что-либо значить — РП думает, что подтверждает
 * заявленное человеком. Янтарный, а не серый: это не справка, а повод
 * посмотреть внимательнее.
 */
function AutofilledBadge({ scheme }: { scheme: Scheme }) {
  const tok = STATUS[scheme].waiting;
  return (
    <span
      title="Часы списала система по правилу распределения затрат — сотрудник их не вводил."
      style={{
        padding: "2px 7px",
        borderRadius: 4,
        background: tok.bg,
        color: tok.fg,
        border: `1px solid ${tok.border}`,
        fontSize: 10.5,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      Заполнено автоматически
    </span>
  );
}

const GRID_TEMPLATE =
  "32px minmax(220px, 1.4fr) 150px 110px minmax(120px, 1fr) minmax(170px, max-content) 260px";

/** Аватар в строке очереди и зазор после него. */
const ROW_AVATAR_SIZE = 28;
const ROW_AVATAR_GAP = 10;

/**
 * На сколько текст строки отступает от левого края своей ячейки: подписи в
 * шапке сдвигаем на столько же, иначе они висят над аватаром, а не над ФИО.
 * Держим одной константой — иначе правка размера аватара разъедет шапку молча.
 */
const ROW_TEXT_OFFSET = ROW_AVATAR_SIZE + ROW_AVATAR_GAP;

/** Колонки, по которым очередь можно упорядочить. */
type SortKey = "employee" | "submitted" | "week" | "hours";

const SORT_ACCESSORS: SortAccessors<TimesheetWeekSummary, SortKey> = {
  employee: (w) => w.employee_name,
  submitted: (w) => w.submitted_at,
  week: (w) => w.week_start,
  hours: (w) => parseFloat(w.allocated_hours ?? "0"),
};

/**
 * Заголовок-кнопка: клик сортирует, повторный — переворачивает порядок.
 *
 * Именно кнопка, а не div с onClick, — чтобы работали Tab и Enter: очередь
 * разбирают в том числе с клавиатуры.
 */
function SortableHead({
  label,
  column,
  sort,
  onSort,
  align = "left",
  defaultDirection = "asc",
  scheme,
}: {
  label: string;
  column: SortKey;
  sort: SortState<SortKey>;
  onSort: (key: SortKey, defaultDirection?: SortDirection) => void;
  align?: "left" | "right";
  defaultDirection?: SortDirection;
  scheme: Scheme;
}) {
  const active = sort.key === column;
  const n = NEUTRALS[scheme];
  // Активность колонки передана только цветом, жирностью и стрелкой — глазами
  // видно, экранной читалке нет. Разметка не табличная, поэтому `aria-sort`
  // здесь недоступен: состояние проговариваем в самой подписи.
  const state = !active
    ? ""
    : sort.direction === "asc"
      ? ", сейчас по возрастанию"
      : ", сейчас по убыванию";
  return (
    <button
      type="button"
      onClick={() => onSort(column, defaultDirection)}
      title={`Сортировать по «${label}»`}
      aria-label={`Сортировать по «${label}»${state}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        width: "100%",
        // Вертикальный отступ — не косметика: в колонке «Сотрудник» две такие
        // кнопки стоят одна над другой, и без запаса промах на пару пикселей
        // переключал бы сортировку на соседнюю колонку. А выбор запоминается,
        // так что очередь открылась бы в неожиданном порядке и в следующий раз.
        padding: "2px 0",
        border: "none",
        background: "none",
        cursor: "pointer",
        font: "inherit",
        letterSpacing: "inherit",
        textTransform: "inherit",
        color: active ? n.text : "inherit",
        fontWeight: active ? 700 : "inherit",
      }}
    >
      <span>{label}</span>
      {/* Стрелка только у активной колонки: показывать у всех — шум. */}
      <span aria-hidden style={{ opacity: active ? 1 : 0, fontSize: 9 }}>
        {sort.direction === "asc" ? "▲" : "▼"}
      </span>
    </button>
  );
}

interface ApprovalRowProps {
  week: TimesheetWeekSummary;
  scheme: Scheme;
  busy: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onOpen: () => void;
  onApprove: () => void;
  onReturn: () => void;
  isLast: boolean;
}

function ApprovalRow({
  week,
  scheme,
  busy,
  selected,
  onToggleSelect,
  onOpen,
  onApprove,
  onReturn,
  isLast,
}: ApprovalRowProps) {
  const n = NEUTRALS[scheme];
  const flags = computeFlags(week);
  // US-74.1: в строке очереди показываем effective_required (норма МИНУС absence),
  // иначе при отпуске Вт+Ср PM видит "16.0 / 32.0" и плашку «Меньше нормы»,
  // хотя сотрудник списал ровно норму (32-16=16).
  const calendarRequired = parseFloat(week.required_hours);
  const absenceHours = parseFloat(week.absence_hours ?? "0") || 0;
  const required = Math.max(0, calendarRequired - absenceHours);
  const allocated = parseFloat(week.allocated_hours);

  return (
    <div
      data-testid={`approval-row-${week.id}`}
      style={{
        display: "grid",
        gridTemplateColumns: GRID_TEMPLATE,
        padding: "11px 14px",
        alignItems: "center",
        borderBottom: isLast ? "none" : `1px solid ${n.border}`,
        fontSize: 12.5,
        gap: 8,
        background: selected ? n.surfaceAlt : "transparent",
      }}
    >
      {/* Select checkbox */}
      <Checkbox
        checked={selected}
        onChange={onToggleSelect}
        aria-label={`Выбрать неделю ${week.employee_name}`}
        size="xs"
      />

      {/* Employee */}
      <button
        type="button"
        onClick={onOpen}
        style={{
          display: "flex",
          alignItems: "center",
          gap: ROW_AVATAR_GAP,
          minWidth: 0,
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
          textAlign: "left",
          color: "inherit",
          fontFamily: "inherit",
        }}
      >
        <Avatar name={week.employee_name} scheme={scheme} size={ROW_AVATAR_SIZE} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500, color: n.text }}>{week.employee_name}</div>
          <div style={{ fontSize: 10.5, color: n.textDim, fontFamily: FONTS.mono }}>
            {formatWeek(week.week_start)}
          </div>
        </div>
      </button>

      {/* Submitted at */}
      <div style={{ fontFamily: FONTS.mono, color: n.textMuted, fontSize: 12 }}>
        {formatSubmittedAt(week.submitted_at)}
      </div>

      {/* Hours */}
      <div
        style={{
          textAlign: "right",
          fontFamily: FONTS.mono,
          fontWeight: 500,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {Number.isFinite(allocated) ? allocated.toFixed(1) : "—"}
        <span style={{ color: n.textDim }}>
          {" / "}
          {Number.isFinite(required) ? required.toFixed(1) : "—"}
        </span>
      </div>

      {/* Flags */}
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
        {/* «— чисто —» значит «отмечать нечего», поэтому оно уместно только
            когда в ячейке действительно пусто. Метки делегирования и
            автозаполнения — тоже отметки: рядом с ними заглушка противоречит
            сама себе. Найдено глазами на приёмке 17.09; в тексте страницы это
            читалось как «— чисто — Заполнено автоматически». */}
        {flags.length === 0 && !week.is_delegated_for_me && !week.autofilled_at ? (
          <span style={{ color: n.textDim, fontSize: 11 }}>— чисто —</span>
        ) : (
          flags.map((f) => <FlagChip key={f.key} flag={f} scheme={scheme} />)
        )}
        {week.is_delegated_for_me && <DelegationBadge scheme={scheme} />}
        {week.autofilled_at && <AutofilledBadge scheme={scheme} />}
      </div>

      {/* Status */}
      <div>
        <StatusBadge
          entity="timesheet"
          code={week.status as TimesheetStatus}
          label={week.status_display}
        />
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <Button size="xs" variant="default" onClick={onReturn} disabled={busy}>
          Вернуть
        </Button>
        <Button
          size="xs"
          color="green"
          leftSection={<Check size={12} />}
          onClick={onApprove}
          loading={busy}
          disabled={busy}
        >
          Согласовать
        </Button>
        <Button size="xs" variant="subtle" onClick={onOpen} aria-label="Открыть превью" px={6}>
          <ExternalLink size={13} />
        </Button>
      </div>
    </div>
  );
}

interface TimesheetApprovalQueueProps {
  variant?: ApprovalVariant;
  /** Optional override for the section title — useful when two queues are stacked. */
  title?: string;
}

export function TimesheetApprovalQueue({
  variant = "lr",
  title,
}: TimesheetApprovalQueueProps = {}) {
  const config = VARIANTS[variant];
  const { colorScheme } = useMantineColorScheme();
  const scheme: Scheme = colorScheme === "dark" ? "dark" : "light";
  const n = NEUTRALS[scheme];

  const [rawItems, setItems] = useState<TimesheetWeekSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [returning, setReturning] = useState<TimesheetWeekSummary | null>(null);
  const [previewing, setPreviewing] = useState<TimesheetWeekSummary | null>(null);
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkApproveResult | null>(null);

  // По умолчанию — кто ждёт дольше всех: очередь разбирают сверху вниз, и
  // первой должна стоять неделя, отправленная раньше остальных.
  const {
    sorted: items,
    sort,
    toggle: toggleSort,
  } = useQueueSort<TimesheetWeekSummary, SortKey>(rawItems, SORT_ACCESSORS, {
    storageKey: `timesheets.${variant}`,
    initial: { key: "submitted", direction: "asc" },
  });

  const reload = useCallback(async () => {
    setError(null);
    try {
      const data = await config.fetchQueue();
      setItems(data);
      setAccessError(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      if (
        message.includes("403") ||
        message.toLowerCase().includes("подчинённых") ||
        message.toLowerCase().includes("проектов")
      ) {
        setAccessError(config.accessDeniedText);
        setItems([]);
      } else {
        setError(message);
      }
    }
  }, [config]);

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, [reload]);

  // Очищаем выбор от строк, которые исчезли из items (после reload).
  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(items.map((i) => i.id));
      const next = new Set<number>();
      prev.forEach((id) => {
        if (ids.has(id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [items]);

  // US-59 #8: бейджи мгновенно после approve/return.
  const { refresh: refreshBadges } = useBadges();

  const handleApprove = useCallback(
    async (week: TimesheetWeekSummary) => {
      setBusyId(week.id);
      try {
        await config.approve(week.id);
        void refreshBadges();
        setPreviewing((current) => (current?.id === week.id ? null : current));
        await reload();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [config, reload, refreshBadges],
  );

  const handleReturn = useCallback(
    async (comment: string) => {
      if (!returning) return;
      const id = returning.id;
      setBusyId(id);
      try {
        await config.approveReturn(id, comment);
        void refreshBadges();
        setReturning(null);
        setPreviewing((current) => (current?.id === id ? null : current));
        await reload();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [config, returning, reload, refreshBadges],
  );

  const toggleSelect = useCallback((id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allSelected = items.length > 0 && selected.size === items.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleSelectAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === items.length ? new Set() : new Set(items.map((i) => i.id)),
    );
  }, [items]);

  const handleBulkApprove = useCallback(async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const result = await config.bulkApprove(ids);
      setBulkResult(result);
      setSelected(new Set());
      void refreshBadges();
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBulkBusy(false);
    }
  }, [config, reload, selected, refreshBadges]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  if (accessError) {
    return (
      <Alert color="gray" variant="light">
        {accessError}
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="baseline">
        <Text fw={600} size="lg">
          {title ?? config.title}
        </Text>
        {!loading && items.length > 0 && (
          <Text size="xs" c="dimmed" ff="monospace">
            {items.length}{" "}
            {items.length === 1
              ? "неделя"
              : items.length >= 2 && items.length <= 4
                ? "недели"
                : "недель"}
          </Text>
        )}
      </Group>

      {error && (
        <Alert color="red" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : items.length === 0 ? (
        <div
          style={{
            padding: "32px 18px",
            background: n.surface,
            border: `1px solid ${n.border}`,
            borderRadius: 8,
            textAlign: "center",
            color: n.textMuted,
          }}
        >
          <Text c="dimmed">Нет недель на согласование.</Text>
        </div>
      ) : (
        <div
          style={{
            border: `1px solid ${n.border}`,
            borderRadius: 8,
            background: n.surface,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: GRID_TEMPLATE,
              padding: "8px 14px",
              borderBottom: `1px solid ${n.border}`,
              fontSize: 11,
              color: n.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              background: n.surfaceAlt,
              fontWeight: 500,
              alignItems: "center",
              gap: 8,
            }}
          >
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleSelectAll}
              aria-label="Выбрать все недели"
              size="xs"
            />
            {/* Период недели рендерится второй строкой под ФИО — в этой же
                колонке, поэтому и кнопка сортировки по нему стоит здесь, а не
                над «Отправлен».

                Подписи идут В СТОЛБИК, повторяя раскладку данных: «Сотрудник»
                над именем, «Период» над датами. Когда они стояли в строку,
                вторая подпись оказывалась над серединой имени — и читалась
                как заголовок пустой колонки (замечено на живой проверке
                07.09; в jsdom это не видно, ширины там нет). */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                minWidth: 0,
                // Сдвигаем под текст: в строке ячейка начинается с аватара,
                // поэтому ФИО и период стартуют правее её левого края.
                paddingLeft: ROW_TEXT_OFFSET,
              }}
            >
              <SortableHead
                label="Сотрудник"
                column="employee"
                sort={sort}
                onSort={toggleSort}
                scheme={scheme}
              />
              <SortableHead
                label="Период"
                column="week"
                sort={sort}
                onSort={toggleSort}
                scheme={scheme}
              />
            </div>
            <SortableHead
              label="Отправлен"
              column="submitted"
              sort={sort}
              onSort={toggleSort}
              scheme={scheme}
            />
            <SortableHead
              label="Часы"
              column="hours"
              sort={sort}
              onSort={toggleSort}
              align="right"
              defaultDirection="desc"
              scheme={scheme}
            />
            <div>Флаги</div>
            <div>Статус</div>
            <div />
          </div>

          {items.map((w, i) => (
            <ApprovalRow
              key={w.id}
              week={w}
              scheme={scheme}
              busy={busyId === w.id}
              selected={selected.has(w.id)}
              onToggleSelect={() => toggleSelect(w.id)}
              onOpen={() => setPreviewing(w)}
              onApprove={() => handleApprove(w)}
              onReturn={() => setReturning(w)}
              isLast={i === items.length - 1}
            />
          ))}
        </div>
      )}

      {bulkResult && (
        <Alert
          color={
            bulkResult.skipped_count === 0 && !(bulkResult.partial_count ?? 0) ? "green" : "yellow"
          }
          withCloseButton
          onClose={() => setBulkResult(null)}
        >
          Одобрено: <b>{bulkResult.approved_count}</b>
          {/* US-87: недели, где вы одобрили свой проект, но ждут других РП. */}
          {(bulkResult.partial_count ?? 0) > 0 && (
            <>
              {" · "}частично (ждут других РП): <b>{bulkResult.partial_count}</b>
            </>
          )}
          {bulkResult.skipped_count > 0 && (
            <>
              {" · "}пропущено: <b>{bulkResult.skipped_count}</b>{" "}
              <Text component="span" size="xs" c="dimmed">
                ({bulkResult.skipped.map((s) => `#${s.id} (${s.reason})`).join(", ")})
              </Text>
            </>
          )}
        </Alert>
      )}

      {selected.size > 0 && (
        <div
          data-testid={`bulk-approve-panel-${variant}`}
          style={{
            position: "sticky",
            bottom: 16,
            zIndex: 5,
            padding: "12px 16px",
            background: n.surface,
            border: `1px solid ${n.borderStrong}`,
            borderRadius: 12,
            boxShadow:
              scheme === "dark" ? "0 12px 32px rgba(0,0,0,0.5)" : "0 12px 32px rgba(20,16,8,0.12)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Text fw={600} size="sm">
            Выбрано: {selected.size}
          </Text>
          <div style={{ flex: 1 }} />
          <Button variant="subtle" size="sm" onClick={clearSelection} disabled={bulkBusy}>
            Снять выбор
          </Button>
          <Button
            color="green"
            size="sm"
            leftSection={<Check size={14} />}
            onClick={handleBulkApprove}
            loading={bulkBusy}
            disabled={bulkBusy}
          >
            Одобрить выбранные ({selected.size})
          </Button>
        </div>
      )}

      <ReturnModal
        week={returning}
        busy={busyId === returning?.id}
        onClose={() => setReturning(null)}
        onConfirm={handleReturn}
      />

      <ApprovalDrawer
        week={previewing}
        variant={variant}
        busy={previewing != null && busyId === previewing.id}
        onClose={() => setPreviewing(null)}
        onApprove={handleApprove}
        onReturn={(w) => setReturning(w)}
      />
    </Stack>
  );
}
